import {
	BrowserAuthError,
	BrowserAuthErrorCodes,
	InteractionRequiredAuthError,
	InteractionType,
	PublicClientApplication,
} from '@azure/msal-browser';
import {
	MsalAuthenticationTemplate,
	MsalProvider as ReactMsalProvider,
} from '@azure/msal-react';
import { PropsWithChildren, useCallback } from 'react';

import { msalConfig, redirectRequest, silentRequest } from 'main/auth/config';

import AuthenticationLoader from '../AuthenticationLoader';
import { MsalAccountSetting } from './MsalAccountSetting';
import { MsalAuthenticationError } from './MsalAuthenticationError';
import { reportMsalError } from './MsalErrorReporter';
import { AuthenticationProvider } from './Provider';

export class MsalProvider extends AuthenticationProvider {
	public static msalInstance = new PublicClientApplication(msalConfig);
	// todo - in future try to refactor to use MsalContext,
	// since it has more properties available, but that would require ReactMsalProvider somewhere higher up the tree

	// Active account is set in MsalAccountSetting effect, which can run after first token requests
	private static getAccount() {
		return (
			MsalProvider.msalInstance.getActiveAccount() ??
			MsalProvider.msalInstance.getAllAccounts()[0]
		);
	}

	public isAuthenticated(): Promise<boolean> {
		return MsalProvider.msalInstance
			.acquireTokenSilent({
				...silentRequest,
				account: MsalProvider.getAccount(),
			})
			.then(() => true)
			.catch(() => false);
	}

	public async getTokenAsync(): Promise<string> {
		return MsalProvider.msalInstance
			.acquireTokenSilent({
				...silentRequest,
				account: MsalProvider.getAccount(),
			})
			.then(({ accessToken }) => accessToken)
			.catch((error: unknown) => {
				const account = MsalProvider.getAccount();
				if (error instanceof InteractionRequiredAuthError && account) {
					MsalProvider.msalInstance
						.acquireTokenRedirect({ ...redirectRequest, account })
						.catch((redirectError: unknown) => {
							// MSAL allows only one redirect, parallel requests are rejected
							if (
								!(redirectError instanceof BrowserAuthError) ||
								redirectError.errorCode !==
									BrowserAuthErrorCodes.interactionInProgress
							) {
								reportMsalError(
									redirectError,
									'acquireTokenRedirect'
								);
							}
						});
				} else {
					reportMsalError(error, 'getTokenAsync');
				}
				return '';
			});
	}

	public getComponent(): ({
		children,
	}: PropsWithChildren) => React.JSX.Element {
		return ({ children }: PropsWithChildren) => {
			const handleOnMsalChanged = useCallback(
				(isMsalInitialized: boolean) => {
					this.onAuthenticatedCallback(isMsalInitialized);
				},
				[]
			);

			return (
				<ReactMsalProvider instance={MsalProvider.msalInstance}>
					<MsalAuthenticationTemplate
						interactionType={InteractionType.Redirect}
						authenticationRequest={redirectRequest}
						errorComponent={MsalAuthenticationError}
						loadingComponent={AuthenticationLoader}>
						<MsalAccountSetting onMsalChanged={handleOnMsalChanged}>
							{children}
						</MsalAccountSetting>
					</MsalAuthenticationTemplate>
				</ReactMsalProvider>
			);
		};
	}
}
